"use client";

import { Html, SmartImage, SmartLink } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { useImageFallback } from "@/Hooks";
import { getSectionDefaultProps } from "@/Templates/Doccure/getSectionDefaultProps";
import { DepartmentsSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import Slider from "react-slick";
import HomeOneSpecialitySectionGenerated, {
  HomeOneSpecialitySectionGeneratedProps,
} from "../Sections/_generated/HomeOneSpecialitySection.generated";
import HomeTwoSpecialitySectionGenerated, {
  HomeTwoSpecialitySectionGeneratedProps,
} from "../Sections/_generated/HomeTwoSpecialitySection.generated";
import { renderGeneratedSection } from "./generated-section-renderer";

type GeneratedDepartmentsRenderer = (
  props: DepartmentsSectionProps,
) => ReactElement;

type HomeOneDepartmentItem =
  HomeOneSpecialitySectionGeneratedProps["specialitySectionSlideZoomOutDowns"][number];
type HomeTwoDepartmentItem =
  HomeTwoSpecialitySectionGeneratedProps["specialitySection346s"][number];

function buildGeneratedDepartmentsProps<T extends Record<string, unknown>>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return getSectionDefaultProps<T>(sectionKey, {
    ...(overrides ?? {}),
  } as Partial<T>);
}

const LegacyDepartmentsSection: FC<DepartmentsSectionProps> = ({
  setting,
  dataSource,
  pageData,
}) => {
  const getImageFallback = useImageFallback();
  const specialitySlider = {
    slidesToShow: 2,
    slidesToScroll: 1,
    dots: false,
    arrows: true,
    infinite: true,
    focusOnSelect: true,
    nextArrow: (
      <div className="spciality-nav nav-bottom owl-nav">
        <button type="button" role="presentation" className="owl-next">
          <FaChevronRight />
        </button>
      </div>
    ),
    prevArrow: (
      <div className="spciality-nav nav-bottom owl-nav">
        <button type="button" role="presentation" className="owl-prev">
          <FaChevronLeft />
        </button>
      </div>
    ),
    responsive: [
      { breakpoint: 576, settings: { slidesToShow: 2 } },
      { breakpoint: 768, settings: { slidesToShow: 3 } },
      { breakpoint: 992, settings: { slidesToShow: 4 } },
      { breakpoint: 1200, settings: { slidesToShow: 8 } },
    ],
  };

  return (
    <section className="speciality-section">
      <div className="container">
        <div
          className="section-header sec-header-one text-center aos"
          data-aos="fade-up"
        >
          <span className="badge badge-primary">{setting.Title}</span>
          <h2>
            <Html value={setting.Description} />
          </h2>
        </div>
        <div
          className="spciality-slider slick-margins slick-arrow-center aos"
          data-aos="fade-up"
        >
          <Slider {...specialitySlider}>
            {dataSource.Items.map((item, index) => (
              <div className="spaciality-item" key={index}>
                <div className="spaciality-img">
                  <SmartLink href={item.Link}>
                    <SmartImage
                      src={getImageFallback({
                        imageUrl: item.Image?.Url,
                        entity: "Department",
                      })}
                      alt={item.Name}
                      width={141}
                      height={189}
                    />
                  </SmartLink>
                </div>
                <h6>
                  <SmartLink href={item.Link}>{item.Name}</SmartLink>
                </h6>
                <p className="mb-0">
                  {item.DoctorsCount}{" "}
                  {pageData.CommonData.DoctorNicknameSingular}
                </p>
              </div>
            ))}
          </Slider>
        </div>
        <div className="spciality-nav nav-bottom owl-nav" />
      </div>
    </section>
  );
};

const generatedDepartmentsRenderers: Record<
  string,
  GeneratedDepartmentsRenderer
> = {
  [CenterOptions.SiteTemplate.SiteTemplateSettings
    .SiteTemplateDepartmentsAppearance.Home1.key]: (props) => {
    const itemMap = new Map(
      props.dataSource.Items.map((item) => [String(item.Id), item] as const),
    );
    const generatedItems = props.dataSource.Items.map<HomeOneDepartmentItem>(
      (item) => ({
        key: String(item.Id),
        src: item.Image?.Url ?? "",
        imageWithBasePath0Src: "",
        link0Text: item.Name,
        p0Text: `${item.DoctorsCount} ${props.pageData.CommonData.DoctorNicknameSingular}`,
        href: item.Link,
      }),
    );

    return (
      <HomeOneSpecialitySectionGenerated
        {...buildGeneratedDepartmentsProps<HomeOneSpecialitySectionGeneratedProps>(
          "HomeOneSpecialitySection",
          {
            sectionDiv0Div0Div0DivText: <Html value={props.setting.Title} />,
            sectionDiv0Div0H20H2Text: <Html value={props.setting.Description} />,
            sectionDiv0Div0H20Span0SpanText: "",
          },
        )}
        specialitySectionSlideZoomOutDowns={generatedItems}
        specialitySectionSlideZoomOutDownsItemWrapper={(item) => {
          const department = itemMap.get(String(item.key));
          if (!department) {
            return null;
          }

          return (
            <div
              className={item.className ?? "slide-item wow zoomOutDown"}
              data-wow-duration={item.dataWowDuration ?? "1s"}
              key={item.key}
            >
              <div className={item.div0ClassName ?? "spaciality-item"}>
                <div className={item.div0ClassName2 ?? "spaciality-img"}>
                  <SmartLink href={department.Link}>
                    <SmartImage
                      src={department.Image?.Url ?? ""}
                      alt={department.Name}
                      width={141}
                      height={189}
                    />
                  </SmartLink>
                </div>
                <h3 className={item.h30ClassName ?? "custom-title"}>
                  <SmartLink href={department.Link}>
                    {department.Name}
                  </SmartLink>
                </h3>
                <p className={item.p0ClassName ?? "mb-0"}>
                  {department.DoctorsCount}{" "}
                  {props.pageData.CommonData.DoctorNicknameSingular}
                </p>
              </div>
            </div>
          );
        }}
      />
    );
  },
  [CenterOptions.SiteTemplate.SiteTemplateSettings
    .SiteTemplateDepartmentsAppearance.Home2.key]: (props) => {
    const listItems = props.dataSource.Items;
    if (listItems.length === 0) {
      return <LegacyDepartmentsSection {...props} />;
    }

    const toRepeatedItem = (
      item: (typeof listItems)[number],
    ): HomeTwoDepartmentItem => ({
      key: String(item.Id),
      dataWowDuration: "1s",
      src: item.Image?.Url ?? "",
      link0Text: item.Name,
      p0Text: `${item.DoctorsCount} ${props.pageData.CommonData.DoctorNicknameSingular}`,
      href: item.Link,
      link0Href: item.Link,
      link0Href2: item.Link,
    });

    return (
      <HomeTwoSpecialitySectionGenerated
        {...buildGeneratedDepartmentsProps<HomeTwoSpecialitySectionGeneratedProps>(
          "HomeTwoSpecialitySection",
          {
            sectionDiv0Div0Div0DivText: <Html value={props.setting.Title} />,
            sectionDiv0Div0H20H2Text: props.setting.Description,
            sectionDiv0Div0H20Span0SpanText: "",
          },
        )}
        specialitySection346s={listItems.map(toRepeatedItem)}
      />
    );
  },
};

const GeneratedDepartmentsSection: FC<DepartmentsSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedDepartmentsRenderers,
    CenterOptions.SiteTemplate.SiteTemplateSettings
      .SiteTemplateDepartmentsAppearance.Home1.key,
    (x) => x.setting.Appearance?.Key,
  );
};

export default GeneratedDepartmentsSection;
